/**
 * FrictionDetector.js - Client-side friction detection utilities
 * 
 * PRIVACY: No PII collected. No backend calls.
 * Detects user friction signals to trigger helpful GIL nudges.
 * 
 * SIGNALS DETECTED:
 * - Hesitation (idle > 8s on key pages)
 * - Back-and-forth navigation (3+ route changes < 20s)
 * - Repeated tooltip/Clarity interactions
 * - Scroll stall (stuck mid-section)
 */

// Friction signal types
export const FRICTION_SIGNALS = {
  HESITATION: 'hesitation',
  BACK_FORTH: 'back_forth',
  REPEATED_TOOLTIPS: 'repeated_tooltips',
  SCROLL_STALL: 'scroll_stall',
  ERROR_ENCOUNTERED: 'error_encountered',
};

// Configuration
const CONFIG = {
  hesitationThreshold: 8000, // 8 seconds
  backForthWindow: 20000, // 20 seconds
  backForthCount: 3, // 3 route changes
  scrollStallDuration: 5000, // 5 seconds stuck
  tooltipInteractionThreshold: 3, // 3 tooltip hovers
  cooldownPeriod: 60000, // 1 minute between nudges
};

class FrictionDetectorClass {
  constructor() {
    this.navigationHistory = [];
    this.lastActivity = Date.now();
    this.tooltipInteractions = 0;
    this.lastNudgeTime = 0;
    this.scrollPosition = 0;
    this.scrollStallTimer = null;
    this.hesitationTimer = null;
    this.listeners = new Set();
    this.isActive = false;
  }

  // Start monitoring
  start() {
    if (this.isActive) return;
    this.isActive = true;
    
    // Track mouse/keyboard activity
    document.addEventListener('mousemove', this.resetActivity);
    document.addEventListener('keydown', this.resetActivity);
    document.addEventListener('click', this.resetActivity);
    document.addEventListener('scroll', this.handleScroll);
    
    // Start hesitation check
    this.startHesitationCheck();
  }

  // Stop monitoring
  stop() {
    this.isActive = false;
    document.removeEventListener('mousemove', this.resetActivity);
    document.removeEventListener('keydown', this.resetActivity);
    document.removeEventListener('click', this.resetActivity);
    document.removeEventListener('scroll', this.handleScroll);
    
    if (this.hesitationTimer) clearInterval(this.hesitationTimer);
    if (this.scrollStallTimer) clearTimeout(this.scrollStallTimer);
  }

  // Reset activity timestamp
  resetActivity = () => {
    this.lastActivity = Date.now();
  };

  // Track navigation
  recordNavigation(path) {
    const now = Date.now();
    this.navigationHistory.push({ path, timestamp: now });
    
    // Keep only recent history
    this.navigationHistory = this.navigationHistory.filter(
      n => now - n.timestamp < CONFIG.backForthWindow
    );
    
    // Check for back-and-forth pattern
    if (this.navigationHistory.length >= CONFIG.backForthCount) {
      const uniquePaths = new Set(this.navigationHistory.map(n => n.path));
      if (uniquePaths.size < this.navigationHistory.length) {
        this.triggerSignal(FRICTION_SIGNALS.BACK_FORTH);
      }
    }
  }

  // Track tooltip/clarity interactions
  recordTooltipInteraction() {
    this.tooltipInteractions++;
    if (this.tooltipInteractions >= CONFIG.tooltipInteractionThreshold) {
      this.triggerSignal(FRICTION_SIGNALS.REPEATED_TOOLTIPS);
      this.tooltipInteractions = 0; // Reset
    }
  }

  // Track errors
  recordError() {
    this.triggerSignal(FRICTION_SIGNALS.ERROR_ENCOUNTERED);
  }

  // Handle scroll events
  handleScroll = () => {
    const currentPosition = window.scrollY;
    
    // Reset scroll stall timer
    if (this.scrollStallTimer) {
      clearTimeout(this.scrollStallTimer);
    }
    
    this.scrollPosition = currentPosition;
    
    // Start new stall detection
    this.scrollStallTimer = setTimeout(() => {
      // Check if still in middle of page (not at top or bottom)
      const scrollHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      const maxScroll = scrollHeight - viewportHeight;
      
      if (currentPosition > 200 && currentPosition < maxScroll - 200) {
        this.triggerSignal(FRICTION_SIGNALS.SCROLL_STALL);
      }
    }, CONFIG.scrollStallDuration);
  };

  // Start hesitation check interval
  startHesitationCheck() {
    this.hesitationTimer = setInterval(() => {
      const idleTime = Date.now() - this.lastActivity;
      if (idleTime > CONFIG.hesitationThreshold) {
        this.triggerSignal(FRICTION_SIGNALS.HESITATION);
      }
    }, 2000); // Check every 2 seconds
  }

  // Trigger friction signal
  triggerSignal(signal) {
    const now = Date.now();
    
    // Cooldown check
    if (now - this.lastNudgeTime < CONFIG.cooldownPeriod) {
      return;
    }
    
    this.lastNudgeTime = now;
    
    // Notify all listeners
    this.listeners.forEach(callback => callback(signal));
  }

  // Subscribe to friction signals
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Reset all state
  reset() {
    this.navigationHistory = [];
    this.tooltipInteractions = 0;
    this.lastNudgeTime = 0;
  }
}

// Singleton instance
export const FrictionDetector = new FrictionDetectorClass();

export default FrictionDetector;
